import { useCallback, useEffect, useMemo, useState } from 'react';
import { supabase } from '@/lib/supabase';
import type { Student } from '@/types';
import { formatNumber } from '@/lib/datetime';
import {
  cycleDueDate,
  excludedCycleKeysOf,
  monthlyFeeAt,
  paidCycleKeysOf,
  parseCycleKey,
  resolveTuitionCycles,
  todayKey,
  type CyclePaymentStatus,
  type MonthlyFeeEntry,
} from '@/lib/tuition';
import TuitionRow from './components/TuitionRow';

type Filter = 'all' | CyclePaymentStatus;

const FILTERS: { key: Filter; label: string; icon: string }[] = [
  { key: 'all', label: '전체', icon: 'ri-list-check' },
  { key: 'overdue', label: '미납', icon: 'ri-error-warning-line' },
  { key: 'upcoming', label: '결제 예정', icon: 'ri-time-line' },
  { key: 'paid', label: '결제완료', icon: 'ri-checkbox-circle-line' },
  { key: 'excluded', label: '회차 제외', icon: 'ri-forbid-line' },
];

interface TuitionRowData {
  student: Student;
  status: CyclePaymentStatus;
  collectDue: string | null;
  collectFee: number;
}

interface FeeRow {
  student_id: string;
  year: number;
  month: number;
  fee: number;
  is_excluded: boolean;
}

interface PaymentRow {
  student_id: string;
  cycle_due: string;
  paid_at: string;
}

export default function TuitionPage() {
  const [students, setStudents] = useState<Student[]>([]);
  const [monthlyFees, setMonthlyFees] = useState<FeeRow[]>([]);
  const [payments, setPayments] = useState<PaymentRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busyId, setBusyId] = useState('');
  const [filter, setFilter] = useState<Filter>('all');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [studentsRes, feeRes, payRes] = await Promise.all([
        supabase.from('students').select('*').order('name', { ascending: true }),
        supabase.from('tuition_monthly_fees').select('student_id, year, month, fee, is_excluded'),
        supabase.from('tuition_payments').select('student_id, cycle_due, paid_at'),
      ]);
      if (studentsRes.error) throw studentsRes.error;
      setStudents(((studentsRes.data as Student[]) ?? []).filter((s) => s.status !== 'ended'));
      setMonthlyFees((feeRes.data as FeeRow[]) ?? []);
      setPayments((payRes.data as PaymentRow[]) ?? []);
    } catch {
      setError('교습비 정보를 불러오지 못했어요.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const rows: TuitionRowData[] = useMemo(() => {
    const today = todayKey();
    const [curYear, curMonth] = today.split('-').map(Number);
    const feesByStudent = new Map<string, MonthlyFeeEntry[]>();
    monthlyFees.forEach((h) => {
      const list = feesByStudent.get(h.student_id) ?? [];
      list.push({ year: h.year, month: h.month, fee: h.fee, is_excluded: h.is_excluded });
      feesByStudent.set(h.student_id, list);
    });
    const paidByStudent = new Map<string, string[]>();
    payments.forEach((p) => {
      const list = paidByStudent.get(p.student_id) ?? [];
      list.push(p.cycle_due);
      paidByStudent.set(p.student_id, list);
    });

    return students.map((student) => {
      const studentFees = feesByStudent.get(student.id) ?? [];
      const effectiveFee = monthlyFeeAt(studentFees, student.tuition_fee || 0, curYear, curMonth);
      const offset = student.tuition_due_month_offset ?? 0;
      const paidCycles: string[] = paidCycleKeysOf(
        (paidByStudent.get(student.id) ?? []).map((cycle_due) => ({ cycle_due })),
        offset,
      );
      const excludedCycles = excludedCycleKeysOf(studentFees);
      const resolved = resolveTuitionCycles(
        student.tuition_due_day,
        paidCycles,
        excludedCycles,
        offset,
      );
      const collectKey = resolved?.collectKey ?? null;
      const dueDay = student.tuition_due_day || 0;
      const collectDue =
        collectKey && dueDay ? cycleDueDate(collectKey, dueDay, offset) : null;
      const collectFee = collectKey
        ? monthlyFeeAt(
            studentFees,
            student.tuition_fee || 0,
            parseCycleKey(collectKey).year,
            parseCycleKey(collectKey).month,
          )
        : effectiveFee;
      const withFee: Student =
        student.tuition_fee === effectiveFee ? student : { ...student, tuition_fee: effectiveFee };
      return {
        student: withFee,
        status: resolved?.status ?? (student.tuition_last_paid_at ? 'paid' : 'upcoming'),
        collectDue,
        collectFee,
      };
    });
  }, [students, monthlyFees, payments]);

  const summary = useMemo(() => {
    let total = 0;
    let paidAmount = 0;
    let unpaidAmount = 0;
    let unpaidCount = 0;
    let paidCount = 0;
    rows.forEach(({ student, status }) => {
      if (status === 'excluded') return;
      const fee = student.tuition_fee || 0;
      total += fee;
      if (status === 'paid') {
        paidAmount += fee;
        paidCount += 1;
      } else if (status === 'overdue') {
        unpaidAmount += fee;
        unpaidCount += 1;
      }
    });
    return { total, paidAmount, unpaidAmount, unpaidCount, paidCount };
  }, [rows]);

  const counts = useMemo(() => {
    const map: Record<Filter, number> = {
      all: rows.length,
      overdue: 0,
      upcoming: 0,
      paid: 0,
      excluded: 0,
    };
    rows.forEach((r) => {
      map[r.status] += 1;
    });
    return map;
  }, [rows]);

  const visible = useMemo(
    () => (filter === 'all' ? rows : rows.filter((r) => r.status === filter)),
    [rows, filter],
  );

  const togglePaid = useCallback(
    async (student: Student, row: TuitionRowData, paid: boolean) => {
      setBusyId(student.id);
      setError('');
      try {
        if (paid) {
          if (!row.collectDue) return;
          const paidAt = todayKey();
          const { error: payError } = await supabase.from('tuition_payments').upsert(
            {
              student_id: student.id,
              cycle_due: row.collectDue,
              amount: row.collectFee,
              paid_at: paidAt,
            },
            { onConflict: 'student_id,cycle_due' },
          );
          if (payError) throw payError;
          await supabase
            .from('students')
            .update({ tuition_last_paid_at: paidAt })
            .eq('id', student.id);
          setStudents((prev) =>
            prev.map((s) => (s.id === student.id ? { ...s, tuition_last_paid_at: paidAt } : s)),
          );
          setPayments((prev) => {
            const others = prev.filter(
              (p) => !(p.student_id === student.id && p.cycle_due === row.collectDue),
            );
            return [...others, { student_id: student.id, cycle_due: row.collectDue as string, paid_at: paidAt }];
          });
        } else {
          if (!row.collectDue) return;
          const { error: delError } = await supabase
            .from('tuition_payments')
            .delete()
            .eq('student_id', student.id)
            .eq('cycle_due', row.collectDue);
          if (delError) throw delError;
          const remaining = payments.filter(
            (p) => !(p.student_id === student.id && p.cycle_due === row.collectDue),
          );
          const remainingForStudent = remaining.filter((p) => p.student_id === student.id);
          const latest =
            remainingForStudent.length > 0
              ? [...remainingForStudent.map((p) => p.paid_at)].sort().slice(-1)[0]
              : null;
          await supabase
            .from('students')
            .update({ tuition_last_paid_at: latest })
            .eq('id', student.id);
          setStudents((prev) =>
            prev.map((s) => (s.id === student.id ? { ...s, tuition_last_paid_at: latest } : s)),
          );
          setPayments(remaining);
        }
      } catch {
        setError('결제 상태 변경에 실패했어요.');
      } finally {
        setBusyId('');
      }
    },
    [payments],
  );

  return (
    <div className="mx-auto w-full max-w-6xl px-4 py-8 md:px-6 md:py-10">
      <div>
        <h1 className="text-2xl font-bold text-foreground-950">교습비 수납 현황</h1>
        <p className="mt-1.5 text-sm text-foreground-600">
          납부 기록을 기준으로 다음 미납 회차를 계산해요. 다음 달분을 미리 낸 경우엔 자동으로
          건너뛰고 그다음 회차를 보여줘요.
        </p>
      </div>

      <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <div className="rounded-xl border border-background-200 bg-background-50 p-5">
          <div className="flex items-center justify-between">
            <span className="text-sm text-foreground-600">이번 달 예정 총액</span>
            <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary-100 text-primary-600">
              <i className="ri-wallet-3-line"></i>
            </span>
          </div>
          <p className="mt-3 text-2xl font-bold text-foreground-950">
            {formatNumber(summary.total)}
            <span className="ml-1 text-base font-medium text-foreground-500">원</span>
          </p>
        </div>

        <div className="rounded-xl border border-background-200 bg-background-50 p-5">
          <div className="flex items-center justify-between">
            <span className="text-sm text-foreground-600">수납 완료</span>
            <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-secondary-100 text-secondary-800">
              <i className="ri-checkbox-circle-line"></i>
            </span>
          </div>
          <p className="mt-3 text-2xl font-bold text-foreground-950">
            {formatNumber(summary.paidAmount)}
            <span className="ml-1 text-base font-medium text-foreground-500">원</span>
          </p>
          <p className="mt-1 text-xs text-foreground-500">{summary.paidCount}명 납부 완료</p>
        </div>

        <div className="rounded-xl border border-background-200 bg-background-50 p-5">
          <div className="flex items-center justify-between">
            <span className="text-sm text-foreground-600">미납 금액</span>
            <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-accent-100 text-accent-700">
              <i className="ri-error-warning-line"></i>
            </span>
          </div>
          <p className="mt-3 text-2xl font-bold text-accent-700">
            {formatNumber(summary.unpaidAmount)}
            <span className="ml-1 text-base font-medium text-foreground-500">원</span>
          </p>
        </div>

        <div className="rounded-xl border border-background-200 bg-background-50 p-5">
          <div className="flex items-center justify-between">
            <span className="text-sm text-foreground-600">미납 인원</span>
            <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-accent-100 text-accent-700">
              <i className="ri-user-unfollow-line"></i>
            </span>
          </div>
          <p className="mt-3 text-2xl font-bold text-foreground-950">
            {summary.unpaidCount}
            <span className="ml-1 text-base font-medium text-foreground-500">명</span>
          </p>
        </div>
      </div>

      <div className="mt-6 flex flex-wrap items-center gap-2">
        {FILTERS.map((f) => (
          <button
            key={f.key}
            type="button"
            onClick={() => setFilter(f.key)}
            className={`flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-xs font-semibold transition-colors whitespace-nowrap ${
              filter === f.key
                ? 'bg-foreground-900 text-background-50'
                : 'border border-background-300 bg-background-50 text-foreground-600 hover:bg-background-100'
            }`}
          >
            <i className={f.icon}></i>
            {f.label}
            <span className="text-[11px] opacity-80">{counts[f.key]}</span>
          </button>
        ))}
      </div>

      {error && !loading && (
        <div className="mt-6 flex items-center justify-between gap-3 rounded-md border border-accent-200 bg-accent-100/70 px-4 py-2.5 text-sm text-accent-800">
          <span className="flex items-center gap-2">
            <i className="ri-error-warning-line"></i>
            {error}
          </span>
          <button
            type="button"
            onClick={load}
            className="rounded-md border border-accent-300 px-3 py-1 text-xs font-semibold text-accent-800 hover:bg-accent-100 whitespace-nowrap"
          >
            다시 시도
          </button>
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-24 text-foreground-500">
          <i className="ri-loader-4-line mr-2 animate-spin text-xl"></i>
          불러오는 중...
        </div>
      ) : visible.length === 0 ? (
        <div className="mt-6 flex flex-col items-center gap-3 rounded-xl border border-dashed border-background-300 bg-background-100 py-20">
          <i className="ri-wallet-3-line text-3xl text-foreground-400"></i>
          <p className="text-sm text-foreground-600">
            {filter === 'all' ? '진행 중인 학생이 없어요.' : '해당하는 학생이 없어요.'}
          </p>
        </div>
      ) : (
        <div className="mt-6 flex flex-col gap-3">
          {visible.map((row) => (
            <TuitionRow
              key={row.student.id}
              student={row.student}
              status={row.status}
              collectDue={row.collectDue}
              collectFee={row.collectFee}
              busy={busyId === row.student.id}
              onTogglePaid={(paid) => togglePaid(row.student, row, paid)}
            />
          ))}
        </div>
      )}
    </div>
  );
}